<template>
	<div class="wrapper">
		<!-- left container: player information -->
		<div class="info">
			<div
				v-for="player in players"
				:key="`tennis-scoreboard-player-info-${player.id}`"
				class="player-info"
			>
				<div class="indicator active" />
				<div class="name">
					{{ player.name }}
				</div>
			</div>
		</div>
		<!-- right container: score -->
		<div class="score">
			<div
				v-for="player in playersWithSets"
				:key="`tennis-scoreboard-score-player-${player.id}`"
				class="player-score"
			>
				<!-- sets with # of won games -->
				<div
					v-for="(set, index) in player.sets"
					:key="`tennis-scoreboard-score-set-${player.id}-${index}`"
					class="set"
					:class="{
						current: !hasEnded && index === sets.length - 1,
					}"
				>
					{{ set.games }}
				</div>
				<!-- current game score in points -->
				<div
					v-if="currentSetHasValidPointValues && !hasEnded"
					class="points"
				>
					{{ getFormattedCurrentPoints(player.id) }}
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name:  'TennisScoreboard',
	props: {
		players: {
			type:     Array,
			required: true,
		},
		sets: {
			type:     Array, // { playerId: number, games: number, points: number }[]
			required: true,
		},
		hasEnded: {
			type:    Boolean,
			default: false,
		},
	},
	computed: {
		currentSet() {
			return this.sets[this.sets.length - 1];
		},
		currentSetPlayerMap() {
			return this.currentSet.reduce((map, set) => {
				map[set.playerId] = set;
				return map;
			}, {});
		},
		playersWithSets() {
			return this.players.map((player) => {
				const sets = this.sets.map((players) => {
					return players.find((item) => item.playerId === player.id);
				});
				return { ...player, sets };
			});
		},
		currentSetHasValidPointValues() {
			return this.currentSet.every((player) => player.points !== '');
		},
	},
	methods: {
		getFormattedCurrentPoints(playerId) {
			const set = this.currentSetPlayerMap[playerId];
			if (typeof set.points === 'string') return set.points;

			let label = set.points;

			if (this.currentSet.reduce((acc, curr) => acc + curr.games, 0) >= 12) {
				label = set.points;
			} else {
				if (set.points === 1) {
					label = '15';
				}
				if (set.points === 2) {
					label = '30';
				}
				if (set.points === 3) {
					label = '40';
				}
				if (set.points >= 4) {
					if (this.currentSet.some((player) => player.points < set.points)) {
						label = 'AD';
					} else {
						label = '40';
					}
				}
			}

			return label;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

$background: var(--color-shadow-grey);
$foreground: var(--color-soft-white);
$set-background: var(--color-grey-850);
$set-border: var(--color-grey-750);
$current-set-background: var(--color-grey-600);
$current-set-border: var(--color-grey-500);
$set-foreground: var(--color-soft-white);
$set-foreground-dark: var(--color-grey-300);
$points-background: var(--color-soft-white);
$points-foreground: var(--color-shadow-grey);
$serving-indicator-background: var(--color-hype-yellow);
$section-height: 52px;

.wrapper {
	display: flex;
	background: $background;
	color: $foreground;
}

.info {
	display: flex;
	flex-direction: column;
	flex: 1;

	.player-info {
		display: flex;
		height: $section-height;
	}

	.indicator {
		width: 4px;
		opacity: 0;

		&.active {
			background: $serving-indicator-background;
		}
	}

	.name {
		display: flex;
		align-items: center;
		padding: 10px 12px;
		font-family: 'Inter';
		font-weight: 500;
		font-size: 28px;
		line-height: 0;
		white-space: nowrap;
	}
}

.score {
	display: flex;
	flex-direction: column;
	align-items: center;
	font-family: 'Inter';
	font-weight: 500;
	font-size: 28px;
	text-align: center;

	.player-score {
		display: flex;
		align-items: center;
		height: $section-height;
		line-height: 0;
	}

	.set {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 44px;
		height: 100%;
		background: $set-background;
		border-right: 1px solid $set-border;
	}
	.set.current {
		background: $current-set-background;
		border-right: 1px solid $current-set-border;
	}
	.points {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 56px;
		height: 100%;
		background: $points-background;
		color: $points-foreground;
	}
}
</style>
